import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { pickBy } from 'lodash-es';
import { combineLatest, map } from 'rxjs';

import { countedShareReplay } from '@blaze-shared/shared-util';
import { themesSelectors } from '@blaze-shared/theming-data-access';
import { LayoutStyleReferencesInfo, StyleSheet } from '@blaze-shared/theming-domain';

import * as StylesActions from './styles.actions';
import {
  loadStylesFinished,
  selectStyleSheetsEntities,
  updateStylePending,
  upsertStyleFinished,
  upsertStylePending,
} from './styles.selectors';

@Injectable({
  providedIn: 'root',
})
export class StyleSheetsFacade {
  private readonly store = inject(Store);

  entities$ = combineLatest([
    this.store.select(selectStyleSheetsEntities),
    this.store.select(themesSelectors.getSelectedThemeId),
    this.store.select(themesSelectors.getAppliedThemeId),
  ]).pipe(
    map(([sheetMap, selectedThemeId, appliedThemeId]) => {
      return pickBy<StyleSheet | undefined>(sheetMap, (sheet) => {
        if (!sheet) {
          return false;
        }
        if (!sheet.themeId) {
          return true;
        }
        if (selectedThemeId) {
          return sheet?.themeId === selectedThemeId;
        }
        if (appliedThemeId) {
          return sheet?.themeId === appliedThemeId;
        }
        return false;
      });
    }),
    countedShareReplay(),
  );

  loadFinished$ = this.store.select(loadStylesFinished);
  upsertPending$ = this.store.select(upsertStylePending);
  updatePending$ = this.store.select(updateStylePending);
  upsertFinished$ = this.store.select(upsertStyleFinished);

  loadStyles(projectId: string, includeReferences?: boolean): void {
    this.store.dispatch(StylesActions.loadStylesSheets({ projectId, includeReferences }));
  }

  delete(styleSheetId: string): void {
    this.store.dispatch(StylesActions.deleteStyleSheet({ id: styleSheetId }));
  }

  updateReferences(pageId: string, references: LayoutStyleReferencesInfo[] | undefined): void {
    if (references === undefined) {
      return;
    }

    this.store.dispatch(StylesActions.updateReferences({ pageId, references }));
  }

  setDefaultInTheme(styleSheetId: string): void {
    this.store.dispatch(StylesActions.setDefaultStyleSheet({ id: styleSheetId }));
  }
}
